import { getFounderInfo } from "@/frontend/features/Company/model/getFounderInfo";
import { getPortfolioCompanyCompetitors } from "@/frontend/features/Company/model/getPortfolioCompanyCompetitors";
import { getPortfolioCompanyListNotes } from "@/frontend/features/Company/model/getPortfolioCompanyListNotes";
import { getPortfolioCompanyMarketOverview } from "@/frontend/features/Company/model/getPortfolioCompanyMarketOverview";
import { getPortfolioCompanyPatents } from "@/frontend/features/Company/model/getPortfolioCompanyPatents";
import { getPortfolioCompanyProducts } from "@/frontend/features/Company/model/getPortfolioCompanyProducts";
import { getPortfolioCompanyProfile } from "@/frontend/features/Company/model/getPortfolioCompanyProfile";
import { getPortfolioCompanySimilarCompanies } from "@/frontend/features/Company/model/getPortfolioCompanySimilarCompanies";
import { getPortfolioCompanyTeam } from "@/frontend/features/Company/model/getPortfolioCompanyTeam";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TParsingStatus } from "@/libs/models/portfolio-companies";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function getPortfolioCompanyData(companyId: number, setIsLoadingRequestAccess: (value: boolean) => void) {
  // company details

  const [detailsDataStatus, setDetailsDataStatus] = useState<TParsingStatus>("processing");
  const {
    data: detailsData,
    isLoading: isLoadingDetailsData,
    isFetching: isFetchingDetailsData,
    refetch: refetchDetailsData,
    error: detailsDataError
  } = useQuery({
    queryKey: ["portfolioCompanyDetails", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanyProfile({ id: companyId });
      setDetailsDataStatus(response.parsingStatus);
      return response;
    },
    refetchInterval: 5000,
    enabled: detailsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (detailsDataError) {
      console.error(detailsDataError);
      setDetailsDataStatus("done");
      reportErrorToSentry(detailsDataError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/company-details");
    }
  }, [detailsDataError]);

  // similar companies
  const [similarCompaniesDataStatus, setSimilarCompaniesDataStatus] = useState<"processing" | "done" | null>(
    "processing"
  );
  const {
    data: similarCompaniesData,
    isLoading: isLoadingSimilarCompaniesData,
    refetch: refetchSimilarCompaniesData,
    error: similarCompaniesError
  } = useQuery({
    queryKey: ["portfolioCompanySimilarCompanies", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanySimilarCompanies({ id: companyId });
      setSimilarCompaniesDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: similarCompaniesDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (similarCompaniesError) {
      console.error(similarCompaniesError);
      setSimilarCompaniesDataStatus("done");
      snackbarConstructor("Failed to fetch similar companies", "error");
      reportErrorToSentry(
        similarCompaniesError,
        "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/similar-companies"
      );
    }
  }, [similarCompaniesError]);

  // competitors

  const [competitorsDataStatus, setCompetitorsDataStatus] = useState<TParsingStatus>("processing");
  const {
    data: competitorCompaniesData,
    isLoading: isLoadingCompetitorCompaniesData,
    refetch: refetchCompetitorCompaniesData,
    error: competitorsError
  } = useQuery({
    queryKey: ["portfolioCompanyCompetitorCompanies", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanyCompetitors(companyId);
      setCompetitorsDataStatus(response.status);

      return response.data;
    },
    refetchInterval: 5000,
    enabled: competitorsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (competitorsError) {
      console.error(competitorsError);
      setCompetitorsDataStatus("done");
      snackbarConstructor("Failed to fetch competitors", "error");
      reportErrorToSentry(
        competitorsError,
        "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/competitor-companies"
      );
    }
  }, [competitorsError]);

  // market overview

  const [marketOverviewDataStatus, setMarketOverviewDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: marketOverviewData,
    isLoading: isLoadingMarketOverviewData,
    refetch: refetchMarketOverviewData,
    error: marketOverviewError
  } = useQuery({
    queryKey: ["portfolioCompanyMarketOverview", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanyMarketOverview({ id: companyId });
      setMarketOverviewDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: marketOverviewDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (marketOverviewError) {
      console.error(marketOverviewError);
      setProductsDataStatus("done");
      snackbarConstructor("Failed to fetch products", "error");
      reportErrorToSentry(marketOverviewError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/products");
    }
  }, [marketOverviewError]);

  // patents

  const {
    data: patentsData,
    isLoading: isLoadingPatentsData,
    refetch: refetchPatentsData,
    error: patentsError
  } = useQuery({
    queryKey: ["portfolioCompanyPatents", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanyPatents({ id: companyId });
      return response;
    },
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (patentsError) {
      console.error(patentsError);
      snackbarConstructor("Failed to fetch patents", "error");
      reportErrorToSentry(patentsError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/patents");
    }
  }, [patentsError]);

  // products
  const [productsDataStatus, setProductsDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: productsData,
    isLoading: isLoadingProductsData,
    refetch: refetchProductsData,
    error: productsError
  } = useQuery({
    queryKey: ["portfolioCompanyProducts", companyId],
    queryFn: async () => {
      const response = await getPortfolioCompanyProducts({ id: companyId });
      setProductsDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: productsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (productsError) {
      console.error(productsError);
      setProductsDataStatus("done");
      snackbarConstructor("Failed to fetch products", "error");
      reportErrorToSentry(productsError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/products");
    }
  }, [productsError]);

  // team by Innovera

  const {
    data: teamData,
    isLoading: isLoadingTeamData,
    refetch: refetchTeamData,
    error: teamError
  } = useQuery({
    queryKey: ["portfolioCompanyTeam", companyId],
    queryFn: () => getPortfolioCompanyTeam({ id: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (teamError) {
      console.error(teamError);
      snackbarConstructor("Failed to fetch team", "error");
      reportErrorToSentry(teamError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/harmonic-people");
    }
  }, [teamError]);

  // notes

  const {
    data: listNotesData,
    refetch: refetchListNotes,
    isLoading: isLoadingListNotes,
    error: listNotesError
  } = useQuery({
    queryKey: ["portfolioCompanyProfilePageListNotes", companyId],
    queryFn: async () => getPortfolioCompanyListNotes({ id: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (listNotesError) {
      console.error(listNotesError);
      snackbarConstructor("Failed to fetch list notes", "error");
      reportErrorToSentry(listNotesError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/notes");
    }
  }, [listNotesError]);

  const {
    data: founderInfoData,
    refetch: refetchFounderInfo,
    error: founderInfoError
  } = useQuery({
    queryKey: ["portfolioCompanyProfileFounderInfoData", companyId],
    queryFn: async () => {
      const res = await getFounderInfo({ id: companyId });
      if (res) setIsLoadingRequestAccess(false);
      return res;
    },
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (founderInfoError) {
      console.error(founderInfoError);
      snackbarConstructor("Failed to fetch founder info", "error");
      reportErrorToSentry(founderInfoError, "PORTFOLIO COMPANY PROFILE: /portfolio-companies/${id}/founder-info");
      setIsLoadingRequestAccess(false);
    }
  }, [founderInfoError, setIsLoadingRequestAccess]);

  return {
    detailsData,
    isLoadingDetailsData,
    isFetchingDetailsData,
    detailsDataStatus,
    refetchDetailsData,
    competitorCompaniesData,
    isLoadingCompetitorCompaniesData,
    refetchCompetitorCompaniesData,
    competitorsDataStatus,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    similarCompaniesDataStatus,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    marketOverviewDataStatus,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    productsDataStatus,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    listNotesData,
    refetchListNotes,
    founderInfoData,
    refetchFounderInfo
  };
}
